// AUTO-GENERATED FILE - DO NOT EDIT DIRECTLY
// Generated from src/charts/syrf-common/env-mapping.yaml
// Run: pnpm run generate:flags

import { createSelector } from '@ngrx/store';
import { selectConfigState } from '@core/state/config/config.selectors';
import { AppConfig } from '@core/services/config/app-config.service';
import { selectShowAdminUiOptions } from '@core/state/ui/admin/admin-ui.selectors';

// Project Settings
/** Enable study filters configuration */
export const selectStudyFiltersConfigurable = createSelector(
  selectConfigState,
  (configState: AppConfig): boolean => configState.studyFiltersConfigurable,
);

/** Enable mode-specific review configuration and combined additional-screening policy */
export const selectReviewEligibilityPolicy = createSelector(
  selectConfigState,
  (configState: AppConfig): boolean => configState.reviewEligibilityPolicy,
);

/** Enable proportional allocation for annotation-stage review slots */
export const selectProportionalStudyAllocation = createSelector(
  selectConfigState,
  (configState: AppConfig): boolean => configState.proportionalStudyAllocation,
);

/** Enable project inclusion/exclusion keyword lists and their highlighting in the integrated PDF viewer */
export const selectScreeningKeywordHighlighting = createSelector(
  selectConfigState,
  (configState: AppConfig): boolean => configState.screeningKeywordHighlighting,
);

/** Enable bulk PDF upload for systematic searches */
export const selectBulkPdfUpload = createSelector(
  selectConfigState,
  (configState: AppConfig): boolean => configState.bulkPdfUpload,
);

/** Enable the auditable project membership disable control (re-enabling a disabled member stays available when off) */
export const selectDisableMembership = createSelector(
  selectConfigState,
  (configState: AppConfig): boolean => configState.disableMembership,
);

/** Enable the search import wizard (reference formats, library folders and optional PDFs) */
export const selectSearchImportWizard = createSelector(
  selectConfigState,
  (configState: AppConfig): boolean => configState.searchImportWizard,
);

// Ui Features
/** Show the contact us form */
export const selectShowContactUsForm = createSelector(
  selectConfigState,
  (configState: AppConfig): boolean => configState.showContactUsForm,
);

/** Enable new question management UI */
export const selectNewQuestionManagement = createSelector(
  selectConfigState,
  (configState: AppConfig): boolean => configState.newQuestionManagement,
);

/** Enable the accelerated annotation form v2 */
export const selectAnnotationFormV2 = createSelector(
  selectConfigState,
  (configState: AppConfig): boolean => configState.annotationFormV2,
);

/** Enable the synchronized integrated study PDF viewer */
export const selectIntegratedPdfViewer = createSelector(
  selectConfigState,
  (configState: AppConfig): boolean => configState.integratedPdfViewer,
);

/** Enable the redesigned stage review page shell */
export const selectStageReviewRedesign = createSelector(
  selectConfigState,
  (configState: AppConfig): boolean => configState.stageReviewRedesign,
);

/** Evaluation only: render the stage review workspace with the Dockview docking layout */
export const selectStageReviewDockview = createSelector(
  selectConfigState,
  (configState: AppConfig): boolean => configState.stageReviewDockview,
);

/** Enable the Study Management Processing page and its contextual links */
export const selectStudyManagementProcessing = createSelector(
  selectConfigState,
  (configState: AppConfig): boolean => configState.studyManagementProcessing,
);

/** Enable new progress indicators */
export const selectNewProgressIndicators = createSelector(
  selectConfigState,
  (configState: AppConfig): boolean => configState.newProgressIndicators,
);

/** Show area charts in stage overview */
export const selectShowStageOverviewAreaCharts = createSelector(
  selectConfigState,
  (configState: AppConfig): boolean => configState.showStageOverviewAreaCharts,
);

/** Enable new stage overview UI */
export const selectNewStageOverview = createSelector(
  selectConfigState,
  (configState: AppConfig): boolean => configState.newStageOverview,
);

/** Enable new screening overview UI */
export const selectNewScreeningOverview = createSelector(
  selectConfigState,
  (configState: AppConfig): boolean => configState.newScreeningOverview,
);

/** Enable version mismatch checking */
export const selectVersionMismatchCheck = createSelector(
  selectConfigState,
  (configState: AppConfig): boolean => configState.versionMismatchCheck,
);

/** Enable SignalR real-time updates and the transport required by active reviewer tracking */
export const selectSignalRActive = createSelector(
  selectConfigState,
  (configState: AppConfig): boolean => configState.signalRActive,
);

/** Enable light/dark theme toggle and dark mode support */
export const selectThemeToggle = createSelector(
  selectConfigState,
  (configState: AppConfig): boolean => configState.themeToggle,
);

/** Restore a signed-out BFF session silently (prompt=none) while the sign-in service session is still valid */
export const selectBffSilentSignIn = createSelector(
  selectConfigState,
  (configState: AppConfig): boolean => configState.bffSilentSignIn,
);

/** Run BFF silent sign-in in a hidden iframe instead of a page navigation, falling back to the navigation when the frame cannot complete */
export const selectBffSilentSignInFrame = createSelector(
  selectConfigState,
  (configState: AppConfig): boolean => configState.bffSilentSignInFrame,
);

/** Saved personal notifications with live updates and reconnect recovery */
export const selectNotificationInbox = createSelector(
  selectConfigState,
  (configState: AppConfig): boolean => configState.notificationInbox,
);

/** Opt-in email for saved notifications; needs notificationInbox and the delivery pause lifted */
export const selectNotificationEmail = createSelector(
  selectConfigState,
  (configState: AppConfig): boolean => configState.notificationEmail,
);

/** Import Gravatar pictures on the server; browsers never load gravatar.com */
export const selectGravatarServerImport = createSelector(
  selectConfigState,
  (configState: AppConfig): boolean => configState.gravatarServerImport,
);

/** Open SyRF dialogs with the Material 3 Expressive (m3e-angular) dialog instead of the Angular Material dialog */
export const selectExpressiveDialogs = createSelector(
  selectConfigState,
  (configState: AppConfig): boolean => configState.expressiveDialogs,
);

/** Reconciliation questions to selected reviewers; deployment-managed until private conversations land */
export const selectStudyAttention = createSelector(
  selectConfigState,
  (configState: AppConfig): boolean => configState.studyAttention,
);

/** In-app notices for confirmed feature releases; needs notificationInbox and runtime releases */
export const selectFeatureReleaseNotices = createSelector(
  selectConfigState,
  (configState: AppConfig): boolean => configState.featureReleaseNotices,
);

/** Show Studies as one workspace: the study list, a Searches pane and the study detail */
export const selectStudiesWorkspace = createSelector(
  selectConfigState,
  (configState: AppConfig): boolean => configState.studiesWorkspace,
);

/** Enable active reviewer tracking, reviewer presence, and slot reservation enforcement */
export const selectActiveReviewerTrackingEnabled = createSelector(
  selectConfigState,
  (configState: AppConfig): boolean => configState.activeReviewerTrackingEnabled,
);

// Rob Features
/** Enable Risk of Bias tool */
export const selectRobToolEnabled = createSelector(
  selectConfigState,
  (configState: AppConfig): boolean => configState.robToolEnabled,
);

/** Show update inclusion info (admin only) */
export const selectShowUpdateInclusionInfo = createSelector(
  selectConfigState,
  (configState: AppConfig): boolean => configState.showUpdateInclusionInfo,
);

// Data Features
/** Enable Graph2Data feature */
export const selectGraph2data = createSelector(
  selectConfigState,
  (configState: AppConfig): boolean => configState.graph2data,
);

/** Delete a deleted account's stored profile pictures from S3 */
export const selectAccountDeletionPictureRemoval = createSelector(
  selectConfigState,
  (configState: AppConfig): boolean => configState.accountDeletionPictureRemoval,
);

// Development
/** Bootstrap the browser app with zoneless change detection */
export const selectZonelessChangeDetection = createSelector(
  selectConfigState,
  (configState: AppConfig): boolean => configState.zonelessChangeDetection,
);

// Monitoring
/** Enable error tracking (Sentry) */
export const selectErrorTracking = createSelector(
  selectConfigState,
  (configState: AppConfig): boolean => configState.errorTracking,
);

/** Enable LogRocket session recording */
export const selectLogRocketEnabled = createSelector(
  selectConfigState,
  (configState: AppConfig): boolean => configState.logRocketEnabled,
);

// Materialized Statistics
/** Serve the screening-only Project Overview surface (FEAT-024 phase 5.1) */
export const selectMaterializedProjectStatisticsProjectOverview = createSelector(
  selectConfigState,
  (configState: AppConfig): boolean => configState.materializedProjectStatisticsProjectOverview,
);

/** Serve the current Stage Overview annotation pie (FEAT-024 phase 5.2 first slice) */
export const selectMaterializedProjectStatisticsStageOverview = createSelector(
  selectConfigState,
  (configState: AppConfig): boolean => configState.materializedProjectStatisticsStageOverview,
);

/** Read immutable project screening observations on Screening Overview (FEAT-024 phase 5) */
export const selectMaterializedProjectStatisticsHistory = createSelector(
  selectConfigState,
  (configState: AppConfig): boolean => configState.materializedProjectStatisticsHistory,
);

/** Show retained membership-stage annotation history in the reviewer dialog */
export const selectMaterializedProjectStatisticsReviewerAnnotationHistory = createSelector(
  selectConfigState,
  (configState: AppConfig): boolean => configState.materializedProjectStatisticsReviewerAnnotationHistory,
);

/** Read immutable stage annotation observations on Stage Overview (FEAT-024 phase 5) */
export const selectMaterializedProjectStatisticsStageHistory = createSelector(
  selectConfigState,
  (configState: AppConfig): boolean => configState.materializedProjectStatisticsStageHistory,
);

/** Read immutable reviewer screening observations in the reviewer history dialog (FEAT-024 phase 5) */
export const selectMaterializedProjectStatisticsReviewerHistory = createSelector(
  selectConfigState,
  (configState: AppConfig): boolean => configState.materializedProjectStatisticsReviewerHistory,
);

/** Serve the signed-in reviewer's current stage progress (FEAT-024 phase 5.3) */
export const selectMaterializedProjectStatisticsReviewerProgress = createSelector(
  selectConfigState,
  (configState: AppConfig): boolean => configState.materializedProjectStatisticsReviewerProgress,
);

/** Use coherent imported search counts in project details and search lists */
export const selectMaterializedProjectStatisticsSearchCounts = createSelector(
  selectConfigState,
  (configState: AppConfig): boolean => configState.materializedProjectStatisticsSearchCounts,
);

/** Use one authorized statistics snapshot for Screening Info charts and leaderboards */
export const selectMaterializedProjectStatisticsScreeningInfo = createSelector(
  selectConfigState,
  (configState: AppConfig): boolean => configState.materializedProjectStatisticsScreeningInfo,
);

/** Use one authorized statistics snapshot for Stage Overview charts and leaderboards */
export const selectMaterializedProjectStatisticsStageOverviewBundle = createSelector(
  selectConfigState,
  (configState: AppConfig): boolean => configState.materializedProjectStatisticsStageOverviewBundle,
);

/** Serve the signed-in reviewer's current project progress (FEAT-024 phase 5.3) */
export const selectMaterializedProjectStatisticsProjectReviewerProgress = createSelector(
  selectConfigState,
  (configState: AppConfig): boolean => configState.materializedProjectStatisticsProjectReviewerProgress,
);

/** Use fresh question-answer counts in question management */
export const selectMaterializedProjectStatisticsQuestionCounts = createSelector(
  selectConfigState,
  (configState: AppConfig): boolean => configState.materializedProjectStatisticsQuestionCounts,
);

/** Serve materialized project statistics to page surfaces (FEAT-024 phase 5.1-5.3) */
export const selectMaterializedProjectStatisticsPages = createSelector(
  selectConfigState,
  (configState: AppConfig): boolean => configState.materializedProjectStatisticsPages,
);

/** Consume materialized statistics client-invalidation revisions over SignalR (FEAT-024 phase 5.4) */
export const selectMaterializedProjectStatisticsSignalR = createSelector(
  selectConfigState,
  (configState: AppConfig): boolean => configState.materializedProjectStatisticsSignalR,
);
